import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor, calculateHeaderGroupHeight } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * How far the page must scroll, in px, before a `sticky="always"` header switches to its
   * scrolled state. A small buffer rather than 0 so rubber-banding at the very top of the page
   * doesn't flicker the bar between its two heights.
   * @constant {number}
   */
  #scrolledThreshold = 40;

  /**
   * A pending re-check of whether the menu still needs to be hidden in favour of the drawer.
   *
   * `<overflow-list>` (`assets/overflow-list.js`) decides this by measuring layout, and it can
   * only measure while it has a box: once `#updateMenuVisibility` hides it via `display: none`
   * on an ancestor, its `ResizeObserver` has nothing left to observe and stops delivering
   * entries, the same way it would for any element that stops being rendered. If the hide was
   * triggered by a transient squeeze — `sections/header.liquid`'s bar, logo, nav and call to
   * action each transition their own size over 0.4s on a scroll-direction change, and a frame
   * partway through that can briefly measure narrower than the settled state — nothing was ever
   * going to ask it to look again on its own, and it would stay hidden until an actual window
   * resize happened to widen past `#menuDrawerHiddenWidth`.
   *
   * So a hide schedules its own second opinion: briefly restore the menu's layout box and let
   * `<overflow-list>` re-measure for real. The delay clears its own 500ms settle debounce with
   * room to spare, so this reads the same settled frame overflow-list itself would.
   * @type {number | null}
   */
  #overflowRecheckTimer = null;

  /**
   * Whether a hide has already been given its one re-check. Without this, a viewport that is
   * genuinely too narrow for the menu — a real fit problem, not a transient one — would hide,
   * optimistically reopen 700ms later, immediately measure the same real shortage, hide again,
   * and repeat forever: the drawer icon flashing next to the nav on a loop instead of sitting
   * still. One attempt distinguishes "this was probably the transient squeeze" from "this
   * viewport doesn't fit the menu"; a hide that survives its re-check falls back to the original
   * behaviour and waits for an actual window resize, same as before this existed. Cleared
   * whenever the menu is shown or the window resizes, so the next genuine hide gets its own
   * attempt. */
  #overflowRecheckAttempted = false;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (this.#overflowRecheckTimer !== null) {
      clearTimeout(this.#overflowRecheckTimer);
      this.#overflowRecheckTimer = null;
    }

    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');

      // See `#overflowRecheckTimer`'s own comment: this is the only path back for a hide that
      // was never real. Optimistic rather than a bare peek — reveal the menu and re-hide the
      // drawer outright, on the assumption that a hide on a bar this wide was the transient
      // squeeze this exists for, not a genuine fit problem. That assumption is what
      // `<overflow-list>` checks next: restoring the class gives it a box to measure again, and
      // if it finds the same squeeze still there, its own `overflowMinimum` event
      // (`#handleOverflowMinimum` below) hides the menu a second time. Optimistic first means the
      // common case — a false alarm — never shows the drawer icon next to the nav even briefly;
      // a genuine narrow-viewport case corrects itself one 500ms debounce later instead.
      //
      // Gated to one attempt per hide (`#overflowRecheckAttempted`) — see that field's own
      // comment for why an ungated version of this loops forever on a viewport that is
      // genuinely too narrow.
      if (!this.#overflowRecheckAttempted) {
        this.#overflowRecheckAttempted = true;
        this.#overflowRecheckTimer = setTimeout(() => {
          this.#overflowRecheckTimer = null;
          if (!this.refs.headerMenu.classList.contains('hidden')) return;

          // A peek, not a verdict: restores the box directly rather than through the `else`
          // branch below, so `#overflowRecheckAttempted` stays set. If this genuinely doesn't
          // fit, `<overflow-list>` says so again (`#handleOverflowMinimum` → this method,
          // `hideMenu: true`) and the guard above stops it scheduling a second attempt — this
          // peek is the one chance a hide gets, not a reset of the count.
          this.refs.headerDrawerContainer.classList.add('desktop:hidden');
          this.#menuDrawerHiddenWidth = null;
          this.refs.headerMenu.classList.remove('hidden');
        }, 700);
      }
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
      this.#overflowRecheckAttempted = false;
    }
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const headerTop = this.getBoundingClientRect().top;
    const isScrollingUp = scrollTop < this.#lastScrollTop;

    /**
     * "Is the page at the top" has two answers here and they stopped agreeing.
     *
     * For `scroll-up` the header is `position: sticky`, so its own rectangle answers it: the
     * header sits at 0 while it is in flow and goes negative once the page has moved past it.
     *
     * For `always` the header is `position: fixed` (`sections/header.liquid`), which means its
     * rectangle reads 0 at every scroll depth — the whole point of taking it out of flow was
     * that it stops moving with the document. Asked the old question, a fixed header answers
     * "still at the top" forever, `data-scroll-direction` never leaves `none`, and everything
     * hanging off that attribute — the bar's height, the mark's size, the call to action —
     * never fires. The document's scroll position is the thing that still changes.
     */
    const isAtTop =
      this.getAttribute('sticky') === 'always' ? scrollTop <= this.#scrolledThreshold : headerTop >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    if (this.#overflowRecheckTimer !== null) {
      clearTimeout(this.#overflowRecheckTimer);
      this.#overflowRecheckTimer = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    /**
     * Measured from the DOM rather than summed from the entries that happened to change.
     *
     * This callback used to reduce over `entries`, which is only the observed elements that
     * resized in that batch — so the total was the height of whatever moved, not the height of
     * the header group. While every observed element resized together nothing showed it. The
     * bar is now `position: fixed` and changes height on its own (`sections/header.liquid`), so
     * a scroll produced a batch holding one entry, the header, which this sum deliberately
     * excludes — and `--header-group-height` was set to **0px** for the rest of the visit.
     *
     * That property is the offset anchor targets clear the header by:
     * `scroll-margin-block-start: var(--header-group-height, …)` in `assets/generator.css` and
     * `snippets/product-information-content.liquid`. At 0 the header's own "Create your bottles"
     * lands the configurator underneath the bar it was clicked from.
     *
     * `calculateHeaderGroupHeight` is the theme's own function for this, exported from
     * `assets/utilities.js` and already used by `updateHeaderHeights()`; the inline script in
     * `layout/theme.liquid` is a third copy of the same loop and says it must be kept in sync
     * with it. This file was the one that had drifted.
     */
    const resizeObserver = new ResizeObserver(() => {
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(calculateHeaderGroupHeight(header, headerGroup));
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
